import { useId } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Spinner } from "@/components/ui/spinner";
import { CreatedTaskPill } from "@/components/ai-elements/created-task-pill";
import type { CreatedTaskReference } from "@/lib/created-task";
import { cn } from "@/lib/utils";
import type { RenderItem } from "@/lib/render-items";
import { ParsedMessageContent } from "@/components/ai-elements/parsed-message";
import { MessageAttachments } from "@/components/ai-elements/message-attachments";
import { conversationMessageDomId } from "@/lib/conversation-headings";

export const STREAMING_WINDOW_SIZE = 3;

export interface ToolGroupCollapsibleProps {
  /** The compacted group render item. */
  item: Extract<RenderItem, { kind: "tool-group" }>;
  /** Overall run lifecycle, independent of the per-item render marker. */
  runStreaming: boolean;
  /** Controlled disclosure state, stored above virtualized rows. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Controlled streaming-window state, stored above virtualized rows. */
  showAll: boolean;
  onShowAllChange: (showAll: boolean) => void;
  /**
   * Render function for a single inner tool item. The parent passes the same
   * renderer used for standalone `kind: "tool"` items so the group reuses
   * the ToolCallBadge + toolDetails hook without knowing about them. The
   * group suppresses created-task pills here and renders them after the
   * disclosure instead. Every other specialized tool row stays at its
   * natural position inside the disclosure.
   */
  renderTool: (
    item: Extract<RenderItem, { kind: "tool" }>,
    options?: { showCreatedTask?: boolean },
  ) => React.ReactNode;
  /** Resolved task references keyed by their inner tool item id. */
  createdTaskFor?: (
    toolId: string,
  ) => CreatedTaskReference | null | undefined;
  /** Opens Markdown file links from an absorbed assistant message. */
  onFileLinkClick?: (url: string, fileName: string) => void;
  renderSurfaceId?: string;
}

export function ToolGroupCollapsible({
  item,
  runStreaming,
  open,
  onOpenChange,
  showAll,
  onShowAllChange,
  renderTool,
  createdTaskFor,
  onFileLinkClick,
  renderSurfaceId,
}: ToolGroupCollapsibleProps) {
  const { t } = useTranslation("chat");
  const generatedId = useId();
  const regionId = `tool-group-${item.id}-${generatedId}`.replace(
    /[^A-Za-z0-9_-]/g,
    "",
  );

  // While streaming, show only the last N inner items (tools and absorbed
  // assistant updates) so the UI doesn't grow vertically with every chained
  // call, unless the user asks to see all of them. When not streaming, every
  // item is visible.
  const visibleItems = runStreaming && !showAll
    ? item.items.slice(-STREAMING_WINDOW_SIZE)
    : item.items;
  const visibleCreatedTasks = visibleItems.flatMap((inner) => {
    if (inner.kind !== "tool") return [];
    const task = createdTaskFor?.(inner.id);
    return task ? [{ toolId: inner.id, task }] : [];
  });
  const summary = t("tool.toolGroup.summary", {
    count: item.toolCount,
    defaultValue: `Used ${item.toolCount} tools`,
  });

  return (
    <>
      <Collapsible
        open={open}
        onOpenChange={onOpenChange}
        className="my-1.5 w-fit max-w-full"
      >
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-expanded={open}
            aria-controls={regionId}
            aria-label={t(open ? "tool.toolGroup.collapse" : "tool.toolGroup.expand", {
              count: item.toolCount,
              name: item.lastToolName,
              defaultValue: open
                ? `Hide ${item.toolCount} tool calls`
                : `Show ${item.toolCount} tool calls`,
            })}
            className="h-7 max-w-full gap-1.5 px-1.5 text-xs hover:bg-muted focus-visible:ring-2"
          >
            <span className="inline-flex items-center gap-1.5 rounded-full border bg-muted/50 px-2 py-0.5 text-xs font-medium text-foreground">
              <Wrench aria-hidden="true" className="size-3 shrink-0 text-muted-foreground" />
              <span className="truncate">{summary}</span>
              {runStreaming && item.lastToolStatus === "start" && (
                <Spinner className="size-3" aria-label={t("tool.running")} />
              )}
            </span>
            <ChevronDown
              aria-hidden="true"
              className={cn(
                "size-3 text-muted-foreground transition-transform",
                open && "rotate-180",
              )}
            />
          </Button>
        </CollapsibleTrigger>

        {open && (
          // Virtua measures this entire group as one dynamic row. A timed
          // height animation produces a stream of intermediate measurements
          // and can move the viewport long after the disclosure action. Keep
          // this virtualized disclosure an atomic size change.
          <CollapsibleContent
            id={regionId}
            role="region"
            aria-label={t("tool.toolGroup.details", {
              defaultValue: "Tool call details",
            })}
            className="pt-1.5 !animate-none"
          >
            <div className="border-l-2 border-muted pl-3" data-testid="tool-group-inner">
              {runStreaming && item.items.length > STREAMING_WINDOW_SIZE && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-expanded={showAll}
                  aria-label={t(
                    showAll ? "tool.toolGroup.showLess" : "tool.toolGroup.showAll",
                    {
                      count: item.toolCount,
                      defaultValue: showAll
                        ? "Show less"
                        : `Show all ${item.toolCount}`,
                    },
                  )}
                  onClick={() => onShowAllChange(!showAll)}
                  className="h-7 gap-1 px-1.5 text-xs text-muted-foreground hover:bg-muted focus-visible:ring-2"
                >
                  {showAll
                    ? t("tool.toolGroup.showLess", {
                        defaultValue: "Show less",
                      })
                    : t("tool.toolGroup.more", {
                        count: item.items.length - STREAMING_WINDOW_SIZE,
                        defaultValue: `+${item.items.length - STREAMING_WINDOW_SIZE} earlier`,
                      })}
                  {showAll ? (
                    <ChevronUp aria-hidden="true" className="size-3" />
                  ) : (
                    <ChevronDown aria-hidden="true" className="size-3" />
                  )}
                </Button>
              )}
              <div>
                {visibleItems.map((inner) =>
                  inner.kind === "tool" ? (
                    <div key={inner.id} className="[&:not(:first-child)]:-mt-2">
                      {renderTool(inner, { showCreatedTask: false })}
                    </div>
                  ) : inner.kind === "message" && inner.role === "assistant" ? (
                    <div
                      key={inner.id}
                      id={renderSurfaceId ? conversationMessageDomId(renderSurfaceId, inner.id) : undefined}
                      data-message-id={inner.id}
                      data-testid={`tool-group-message-${inner.id}`}
                      className="my-2 rounded-md bg-muted/40 px-2 py-1.5 text-sm"
                    >
                      <ParsedMessageContent
                        text={inner.text}
                        onFileLinkClick={onFileLinkClick}
                        messageId={inner.id}
                        renderSurfaceId={renderSurfaceId}
                      />
                      {inner.files.length > 0 && (
                        <MessageAttachments
                          files={inner.files}
                          variant="pill"
                          className={inner.text ? "mt-2" : undefined}
                          onFileClick={onFileLinkClick}
                        />
                      )}
                    </div>
                  ) : null,
                )}
              </div>
            </div>
          </CollapsibleContent>
        )}
      </Collapsible>

      {visibleCreatedTasks.map(({ toolId, task }) => (
        <CreatedTaskPill
          key={toolId}
          task={task}
          className="mb-1.5 ml-1.5 mt-0.5"
        />
      ))}
    </>
  );
}
